import { useCallback, useEffect, useMemo, useState } from "react";
import CrudPage from "@/components/CrudPage";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function OutboundCallPins() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const [permissionClasses, setPermissionClasses] = useState([]);
  const [tenants, setTenants] = useState([]);

  const loadSupportData = useCallback(async () => {
    const unscoped = isSuperAdmin ? { skipTenantScope: true } : undefined;
    const requests = [api.get("/call-permissions", unscoped)];
    if (isSuperAdmin) requests.push(api.get("/tenants", { skipTenantScope: true }));
    const [permissionsResponse, tenantsResponse] = await Promise.all(requests);
    setPermissionClasses(permissionsResponse.data || []);
    setTenants(tenantsResponse?.data || []);
  }, [isSuperAdmin]);

  useEffect(() => {
    loadSupportData().catch(() => {
      setPermissionClasses([]);
      setTenants([]);
    });
  }, [loadSupportData]);

  const tenantOptions = useMemo(
    () => (tenants || []).map((tenant) => ({ value: tenant.id, label: tenant.nome })),
    [tenants],
  );
  const tenantMap = useMemo(
    () => Object.fromEntries((tenants || []).map((tenant) => [tenant.id, tenant.nome])),
    [tenants],
  );
  const permissionMap = useMemo(
    () => Object.fromEntries((permissionClasses || []).map((item) => [item.id, item])),
    [permissionClasses],
  );

  const fields = useMemo(() => [
    ...(isSuperAdmin ? [{
      key: "tenant_id",
      label: "Cliente",
      type: "select",
      full: true,
      options: () => [{ value: "", label: "Selecione o cliente" }, ...tenantOptions],
      onChange: (next, value) => ({ ...next, tenant_id: value, permission_class_id: "" }),
    }] : []),
    { key: "nome", label: "Nome do PIN", full: true, placeholder: "Ex.: Supervisao externa" },
    { key: "pin", label: "PIN de saida", mono: true, placeholder: "12345", hint: "Use exatamente 5 digitos." },
    {
      key: "permission_class_id",
      label: "Classe de permissao",
      type: "select",
      full: true,
      options: (form) => {
        const tenantId = form.tenant_id || user?.tenant_id;
        return [
          { value: "", label: "-- selecione a classe --" },
          ...permissionClasses
            .filter((item) => item.enabled !== false && (!tenantId || item.tenant_id === tenantId))
            .map((item) => ({ value: item.id, label: item.nome })),
        ];
      },
      hint: "A classe escolhida define quais chamadas externas este PIN pode completar e por qual rota sair.",
    },
    { key: "descricao", label: "Descricao", type: "textarea", full: true, placeholder: "Quem usa este PIN e em qual situacao." },
    { key: "enabled", label: "Ativo", type: "toggle", full: true },
  ], [isSuperAdmin, permissionClasses, tenantOptions, user?.tenant_id]);

  return (
    <CrudPage
      title="PIN de ligacao externa"
      subtitle="Controle por PIN"
      endpoint="/outbound-call-pins"
      testidPrefix="outbound-call-pins"
      fields={fields}
      headers={[...(isSuperAdmin ? ["Cliente"] : []), "Nome", "PIN", "Classe de permissao", "Status"]}
      defaultValues={{ tenant_id: user?.tenant_id || "", nome: "", pin: "", permission_class_id: "", descricao: "", enabled: true }}
      transformSubmit={(form) => ({
        ...form,
        tenant_id: isSuperAdmin ? (form.tenant_id || "") : (user?.tenant_id || form.tenant_id || ""),
        pin: String(form.pin || "").replace(/\D/g, "").slice(0, 5),
      })}
      renderRow={(row) => <>
        {isSuperAdmin && <td>{tenantMap[row.tenant_id] || row.tenant_id || "-"}</td>}
        <td>
          <div className="font-medium">{row.nome || "-"}</div>
          <div className="text-[10px] text-[color:var(--text-muted)]">{row.descricao || "-"}</div>
        </td>
        <td className="font-mono">{row.pin || "-"}</td>
        <td>{row.permission_class_id ? <span className="sbc-tag tag-online">{permissionMap[row.permission_class_id]?.nome || "Classe vinculada"}</span> : <span className="text-xs text-[color:var(--text-muted)]">Sem classe</span>}</td>
        <td>{row.enabled ? <span className="sbc-tag tag-online">Ativo</span> : <span className="sbc-tag tag-offline">Inativo</span>}</td>
      </>}
    />
  );
}
